// -----------------------------------------------------------------------------
// CompleteTreatmentConfirmDialog
//
// Confirmation step for the attended / consultation popups' footer "Treatment
// complete" button. Treatment-close is irreversible (POST treatment_closed), so
// the footer click opens this dialog and the actual CompleteTreatment dispatch
// only fires on Confirm. Mirrors the warning the treatment-started hub shows.
//
// Props:
//   open        — dialog visibility
//   onCancel    — Cancel / backdrop close → abort, no mutation
//   onConfirm   — Confirm → dispatch CompleteTreatment (owned by the parent)
//   submitting  — true while the POST is in flight; disables both buttons
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { Box, Dialog, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';

export default function CompleteTreatmentConfirmDialog({ open, onCancel, onConfirm, submitting }) {
    return (
        <Dialog
            data-testid="complete-treatment-confirm-dialog"
            open={open}
            onClose={() => { if (!submitting) onCancel(); }}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}
        >
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                    Complete treatment
                </Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    Mark this treatment as completed?
                </Typography>
            </Box>
            <DialogContent sx={{ px: 3, pt: 2, pb: 1.5 }}>
                <Box sx={{
                    display: 'flex',
                    gap: 1.25,
                    p: 2,
                    bgcolor: 'rgba(255, 149, 0, 0.08)',
                    borderLeft: '3px solid #ff9500',
                    borderRadius: '8px',
                }}>
                    <Iconify icon="bxs:info-circle" width={18} height={18} sx={{ color: '#ff9500', mt: 0.125, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '13px', color: '#1d1d1f', letterSpacing: '-0.01em', lineHeight: 1.45 }}>
                        This closes the treatment record and can&apos;t be undone.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Button
                    data-testid="complete-treatment-confirm-cancel"
                    onClick={onCancel}
                    disabled={submitting}
                    sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    data-testid="complete-treatment-confirm-yes"
                    variant="contained"
                    disableElevation
                    loading={submitting}
                    loadingPosition="start"
                    onClick={onConfirm}
                    sx={{
                        bgcolor: '#7B61FF',
                        color: '#fff',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        py: 0.75,
                        px: 2.5,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#6A4FE0', boxShadow: 'none' },
                        '&.Mui-disabled': { bgcolor: 'rgba(123,97,255,0.4)', color: '#fff' },
                    }}
                >
                    Mark complete
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

CompleteTreatmentConfirmDialog.propTypes = {
    open: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
};

CompleteTreatmentConfirmDialog.defaultProps = {
    open: false,
    submitting: false,
};
